import { cn } from "@/lib/utils";
import { bucketUrlPrefix } from "common/config";
import { AccessibilityCategory, AccessibilityTag, ReviewPhoto, VenueDetails } from "common/types";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { PiFlagPennant } from "react-icons/pi";
import Slider from "react-slick";
import FullImageViewer from "@/components/FullImageViewer";
import AnalyticsService from "@/services/analytics";
import { useI18N } from "common/hooks";
import { createPortal } from "react-dom";

const SafeSlider = Slider as any; 

function SampleNextArrow(props: any) {
  const { onClick } = props;
  return (
    <div
      role="button"
      className="absolute right-[40px] bottom-[calc(50%-20px)] border rounded-full"
      onClick={onClick}
    >
      <img className="w-8 h-8" src="/images/Main Icon Buttons (1).svg" alt="" />
    </div>
  );
}

function SamplePrevArrow(props: any) {
  const { onClick } = props;
  return (
    <div
      role="button"
      className="absolute bottom-[calc(50%-20px)] left-[40px] z-10 border rounded-full"
      onClick={onClick}
    >
      <img className="w-8 h-8" src="/images/Main Icon Buttons.svg" alt="" />
    </div>
  );
}

type AccessibilitySubItemProps = {
  qtagsAll: string;
  qtags: AccessibilityTag[];
  sections: any;
  reviewPhotos: ReviewPhoto[];
  fullSize?: boolean;
  venueId: string;
  venueDetails: VenueDetails;
  onFlag: (mainTag) => void;
  lastUpdated: string;
  categories: AccessibilityCategory[];
};

function AccessibilitySubItem({
  qtags,
  qtagsAll,
  sections,
  venueDetails,
  reviewPhotos,
  fullSize,
  onFlag,
  lastUpdated,
}: AccessibilitySubItemProps) {
  const subMap = new Map<string, string[]>();

  const i18n = useI18N();

  const [isOpen, setIsOpen] = useState(false);

  const [fullImageView, setFullImageView] = useState<{
    show: boolean;
    current: number;
    title: string;
  }>({
    show: false,
    current: 0,
    title: "",
  });

  const tags = qtagsAll.split(",");
  const mainTag = tags[0];
  const photos = reviewPhotos;

  // const photos = venueDetails?.businessPhotos.filter(
  //   (photo) => photo.tagName === mainTag
  // );

  const handleSubItemTracking = (mainTag, isOpen) => {
    if (isOpen) {
      AnalyticsService.trackGAEventHandler("accessibilityItemClose");
      AnalyticsService.trackMx("accessibilityItemClose", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
        MainTag: mainTag,
      });
    } else {
      AnalyticsService.trackGAEventHandler("accessibilityItemOpen");
      AnalyticsService.trackMx("accessibilityItemOpen", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
        MainTag: mainTag,
      });
    }
  };

  const handleOpenFlagTracking = () => {
    AnalyticsService.trackGAEventHandler("flagAccessibilityReportOpen");
    AnalyticsService.trackMx("flagAccessibilityReportOpen", {});
  };

  for (const key in sections) {
    const qtag = qtags.filter(
      (t) => t.categoryDisplaySectionId?.toString() === key
    );
    if (qtag.length === 0) continue;
    subMap.set(
      sections[key],
      qtag.map((t) => t.tagName)
    );
  }

  // Custom logic for 'No bathroom onsite'
  /*
  if (qtags.length > 0 && qtags[0].tagId === 743) {
    return (
      <div className="bg-primary-100 dark:bg-[#37354D] rounded-[12px] px-[14px] md:px-[18px] py-[10px] md:py-[12px]">
        <div className="cursor-pointer flex flex-row items-center" tabIndex={0}>
          {
            <div className="w-[24px] h-[24px] relative">
              <img
                src={bucketUrlPrefix + qtags[0]?.tag_image}
                alt={`${mainTag} icon`}
                aria-hidden="true"
                className="w-[24px] h-[24px] mr-2 filter dark:invert dark:brightness-0"
              />
            </div>
          }
          <p className="ml-4 flex-1" aria-hidden="true">
            {mainTag}
          </p>
        </div>
        <div className="flex items-end justify-end">
          <div className="bg-primary-100 dark:bg-[#37354D] pl-[8px] pt-[8px] rounded-tl-[12px]">
            <div
              onClick={() => {
                const results = qtags.filter((t) => t.tagName === mainTag);
                onFlag(results[0]);
              }}
              className="cursor-pointer flex flex-row justify-center items-center w-[50px] h-[50px] rounded-[12px] border-2 border-error-900 dark:border-error-200 bg-primary-50 dark:bg-dark-800"
              tabIndex={0}
              role="button"
              aria-label="Flag accessibility"
            >
              <PiFlagPennant
                size={24}
                className="text-error-900 dark:text-error-200"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    );
  } */

  return (
    <div className="bg-primary-100 dark:bg-[#37354D] rounded-[12px] px-[14px] md:px-[18px] py-[10px] md:py-[12px]">
      <div
        onClick={() => {
          handleSubItemTracking(mainTag, isOpen);
          setIsOpen(!isOpen);
        }}
        className="cursor-pointer flex flex-row items-center"
        tabIndex={0}
      >
        {qtags.length > 0 && qtags[0].tagId === 743 && !isOpen ? (
          // Custom logic for 'No bathroom onsite'
          <div className="w-[100px] h-[100px] rounded-[12px] bg-white dark:bg-primary-900 flex items-center justify-center">
            <img
              src={bucketUrlPrefix + qtags[0]?.tag_image}
              alt={`${mainTag} icon`}
              aria-hidden="true"
              className="w-[60px] h-[60px] rounded-[12px] object-cover filter dark:invert dark:brightness-0"
            />
          </div>
        ) : (
          photos &&
          photos.length > 0 &&
          !isOpen && (
            <img
              src={bucketUrlPrefix + photos[0].path}
              className="w-[100px] md:w-[100px] h-[100px] rounded-[12px] object-cover"
              aria-hidden="true"
            />
          )
        )}
        <p className="ml-4 flex-1 text-primary-800 dark:text-primary-150">{mainTag}</p>
        <div className="sm:mr-1 md:mr-4">
          <button
            className={cn(
              "text-primary-800 dark:text-primary-100 ml-2 duration-200",
              isOpen ? "rotate-180" : ""
            )}
            aria-label={`${isOpen ? "Close" : "Open"} ${mainTag} accessibility tags`}
          >
            <ChevronDown aria-hidden="true" role="none" />
          </button>
        </div>
      </div>
      {isOpen && (
        <>
          <div
            className={cn("my-4 pb-6", fullSize ? "" : " max-w-[100%]")}
            // style={{
            //   width: fullSize
            //     ? width > 820
            //       ? width - 380
            //       : width - 110
            //     : width > 800 //condition to check sm or md
            //       ? width / 2 - 110 //normal
            //       : width - 80, //Mobile iew
            // }}
            aria-hidden="true"
          >
            <SafeSlider
              dots={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              dotsClass="slick-dots custom-dots"
              className="custom-slider"
              nextArrow={photos.length > 1 && <SampleNextArrow />}
              prevArrow={photos.length > 1 && <SamplePrevArrow />}
              infinite={photos.length > 1}
              arrows={photos.length > 1}
            >
              {photos.map((image, index) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-2xl overflow-clip max-h-[200px] w-[100%]",
                    fullSize ? "md:max-h-[350px]" : "md:max-h-[250px]"
                  )}
                  onClick={() => {
                    setFullImageView({
                      show: true,
                      current: index,
                      title: mainTag,
                    });
                    
                    // Track opening the full image viewer
                    AnalyticsService.trackGAEventHandler("openPhotoLightbox");
                    AnalyticsService.trackMx("openPhotoLightbox", {
                      VenueId: venueDetails.business.id,
                      VenueBrandId: venueDetails.brandId,
                      VenueBrandName: venueDetails.brandName,
                      VenueAddress: venueDetails.address,
                      VenueName: venueDetails.name,
                      VenueType: venueDetails.businessType,
                      CategoryName: "Accessibility",
                      SubcategoryName: mainTag,
                    });
                  }}
                >
                  <img
                    className={cn(
                      "rounded-2xl overflow-clip max-h-[200px] object-cover w-full",
                      fullSize ? "md:max-h-[350px]" : "md:max-h-[250px]"
                    )}
                    src={bucketUrlPrefix + image.path}
                    alt={image.altText}
                  />
                </div>
              ))}
            </SafeSlider>
          </div>
          {qtags.length > 0 && qtags[0].tagId !== 743 && (
            <div className="rounded-[12px] bg-white dark:bg-primary-900 py-[12px]">
              {Array.from(subMap.entries()).map(([key, values], index) => {
                // Check if all values match the mainTag
                const allValuesMatchMainTag = values.every(
                  (value) => value === mainTag
                );

                return (
                  <div
                    key={key}
                    className={cn(
                      "px-[18px]",
                      index !== Array.from(subMap.entries()).length - 1
                        ? "border-b border-b-primary-150 dark:border-b-primary-300"
                        : ""
                    )}
                  >
                    {!allValuesMatchMainTag && (
                      <p
                        className="button-small-semi-bold !text-primary-500 dark:!text-primary-300 pt-2"
                        tabIndex={0}
                      >
                        {key === "Main Area" ? "General" : `${key}:`}
                      </p>
                    )}

                    <ul className="list-disc px-6 mb-2" role="none">
                      {values.map((value, index) => {
                        if (mainTag === value) return null;
                        return (
                          <li
                            className="paragraph-small-medium text-primary-800 dark:text-primary-150 my-1"
                            key={index}
                            tabIndex={0}
                            arial-label={value}
                          >
                            {value}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          <div className="rounded-[12px] bg-primary-50 dark:bg-dark-800 flex mt-3 border-2 border-primary-200">
            <div className="p-2 flex flex-row items-center justify-between w-[100%]">
              <div className="ml-4 text-primary-800 dark:text-primary-150">{`${i18n.get("lastUpdated")}: ${lastUpdated}`}</div>
              <div
                onClick={() => {
                  const results = qtags.filter((t) => t.tagName === mainTag);
                  handleOpenFlagTracking();
                  onFlag(results[0]);
                }}
                className="cursor-pointer flex flex-row justify-center items-center w-[50px] h-[50px] rounded-[12px] border-2 border-error-900 dark:border-error-200 bg-primary-50 dark:bg-dark-800"
                tabIndex={0}
                role="button"
                aria-label="Flag accessibility"
              >
                <PiFlagPennant
                  size={24}
                  className="text-error-900 dark:text-error-200"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </>
      )}
      {fullImageView.show && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90">
          <FullImageViewer
            images={photos.map((photo) => ({
              src: bucketUrlPrefix + photo.path,
              alt: photo.alt_text || "",
              tag_name: photo.tag_name,
              category_name: photo.tag_applied,
              created_at: photo.created_at || "",
              width: photo.width,
              height: photo.height,
              // Add tags for accessibility information
              qtags: qtags.map(tag => ({
                tagId: tag.tagId,
                tagName: tag.tagName,
                categoryDisplaySectionId: tag.categoryDisplaySectionId?.toString()
              })),
              tags: qtags.map(tag => ({
                tagName: tag.tagName,
                categoryDisplaySectionId: tag.categoryDisplaySectionId?.toString()
              })),
              // Add display sections from the venue
              displaySections: sections,
              // Add venue details for tracking
              venueId: venueDetails.business.id,
              venueName: venueDetails.name,
              venueAddress: venueDetails.address,
              venueBrandId: venueDetails.brandId,
              venueBrandName: venueDetails.brandName,
              venueTypes: venueDetails.businessType
            }))}
            currentIndex={fullImageView.current}
            onClose={() => {
              setFullImageView({
                show: false,
                current: 0,
                title: "",
              });
            }}
            onFlag={() => {
              const results = qtags.filter((t) => t.tagName === mainTag);
              handleOpenFlagTracking();
              onFlag(results[0]);
            }}
            showFlag={true}
            showRightTab={true} 
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default AccessibilitySubItem;
